import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import type { RefObject } from 'react'

import {
  useCallback,
  useContext,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'

import * as React from 'react'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CruiseLineMobileInput from './CruiseLineSelectMobile'
import type { CruiseLineSelectDesktopRef } from './CruiseLineSelectDesktop'
import CruiseLineSelectDesktop from './CruiseLineSelectDesktop'
import { trackClientEvent } from 'analytics/analytics'

export interface CruiseLineSelectRef {
  focus: () => void
}

interface Props {
  onChange: (values: App.CruiseGlobalFilters) => void
  dropdownAnchorRef: RefObject<HTMLElement | null>
  drawerMode?: boolean
}

const CruiseLineSelect = forwardRef<CruiseLineSelectRef, Props>((props, ref) => {
  const { onChange, drawerMode, dropdownAnchorRef } = props
  const desktopInputRef = useRef<CruiseLineSelectDesktopRef>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      desktopInputRef.current?.focus()
    },
  }), [])
  const [searchTerm, setSearchTerm] = useState('')
  const { cruiseLines: searchCruiseLines = [] } = useContext(GlobalSearchStateContext)
  const [facetsV2] = useCruiseSearchFacets({
    facetTypes: ['cruise_lines'],
    offerSource: 'cruises',
  })

  const cruiseLineNames = useMemo(() => {
    return facetsV2.cruiseLines
      .filter(({ slug }) => searchCruiseLines.includes(slug))
      .map(({ label }) => label)
  }, [searchCruiseLines, facetsV2.cruiseLines])

  const handlerSearchTerm = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    trackClientEvent({
      subject: `Filter input - Cruise Lines - ${e.target.value}`,
      action: 'input',
      category: 'cruise-search-filters',
      type: 'interaction',
    })
    setSearchTerm(e.target.value)
  }, [])

  const handlerClearSearchTerm = useCallback(() => {
    setSearchTerm('')
  }, [])

  return (
    <div>
      <CSSBreakpoint max="mobile">
        <CruiseLineMobileInput
          onChange={onChange}
          cruiseLineNames={cruiseLineNames}
          handlerSearchTerm={handlerSearchTerm}
          handlerClearSearchTerm={handlerClearSearchTerm}
          searchTerm={searchTerm}
        />
      </CSSBreakpoint>

      <CSSBreakpoint min="tablet">
        {drawerMode && <CruiseLineMobileInput
          onChange={onChange}
          cruiseLineNames={cruiseLineNames}
          drawerMode={drawerMode}
          handlerSearchTerm={handlerSearchTerm}
          handlerClearSearchTerm={handlerClearSearchTerm}
          searchTerm={searchTerm}
        />}

        {!drawerMode && <CruiseLineSelectDesktop
          ref={desktopInputRef}
          onChange={onChange}
          cruiseLineNames={cruiseLineNames}
          dropdownAnchorRef={dropdownAnchorRef}
          handlerSearchTerm={handlerSearchTerm}
          handlerClearSearchTerm={handlerClearSearchTerm}
          searchTerm={searchTerm}
        />}
      </CSSBreakpoint>
    </div>
  )
})

CruiseLineSelect.displayName = 'CruiseLineSelect'

export default CruiseLineSelect
